import authService from './authService';
import { ApiResponse } from '../types/common';
import type {
  FinancialAccount,
  TransactionsStatement,
  FinancialTransaction,
  TransactionCreatePayload,
  TransactionUpdatePayload,
  TransactionSupplierLinkPayload,
  GroupScope,

  CreditCard,
  CreditCardFormData,
  CreditCardInvoicesResponse,
  CreditCardProjection,
  CreditCardInvoiceTransactionsResponse,
  CreditCardTransaction,
  CreditCardTransactionCreatePayload,
  CreditCardTransactionUpdatePayload,
  InvoiceUploadReview,
  InvoiceUploadConfirmItem,
  InvoiceUploadConfirmResult,
  RecurringTransaction,
  RecurringTransactionPayload,
  RecurringDeleteMode,
  RecurringType,
  StatementImportResult,
  ImportCorrelation,
  ImportCorrelationPayload,
  PixReceivable,
  PixFeeSummary,
  AccountingPlanNode,
  DreGroup,
  AccountingPlanCreatePayload,
  AccountingPlanUpdatePayload,
  ProductEarnRow,
} from '../types/financial';

/**
 * Financial Service — módulo Financeiro
 *
 * Conta Corrente, Cartões de Crédito, Lançamentos Recorrentes, Importação de
 * Extrato, Plano de Contas e Margem de Produtos.
 * Todos os endpoints usam o envelope {success, data}.
 */
class FinancialService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  private async getBaseUrl(): Promise<string> {
    const { getConfig } = await import('../config');
    return getConfig().api.baseUrl;
  }

  /** Extrai a mensagem de erro do corpo da resposta (ou usa o fallback). */
  private async throwApiError(response: Response, fallback: string): Promise<never> {
    let message = fallback;
    try {
      const body = await response.json();
      message = body?.message || body?.error || fallback;
    } catch {
      /* corpo não-JSON — mantém fallback */
    }
    throw new Error(message);
  }

  /**
   * POST multipart/form-data com fallback de autenticação (cookie → token),
   * seguindo o padrão de materialReceiptService.reportDamage.
   */
  private async postMultipart(url: string, fd: FormData, fallbackError: string): Promise<Response> {
    const baseUrl = await this.getBaseUrl();

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}${url}`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      await this.throwApiError(response, fallbackError);
    }
    return response;
  }

  /**
   * Download de arquivo (CSV) via fetch → blob → link temporário,
   * seguindo o padrão de salesReportService.downloadPdf.
   */
  private async downloadFile(url: string, fallbackFilename: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}${url}`, {
        method: 'GET',
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      await this.throwApiError(response, `Erro ao exportar arquivo (${response.status})`);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] ?? fallbackFilename;

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(objectUrl);
  }

  // ─── Conta Corrente — contas e lançamentos ─────────────────────

  /** GET /financial/accounts?companyId= */
  async getAccounts(companyId: number): Promise<FinancialAccount[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/financial/accounts?companyId=${companyId}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar contas');
    const data: ApiResponse<FinancialAccount[]> = await response.json();
    return data.data ?? [];
  }

  /** GET /financial/transactions?accountId=&year=&month= */
  async getTransactions(accountId: number, year: number, month: number): Promise<TransactionsStatement> {
    const params = new URLSearchParams({
      accountId: accountId.toString(),
      year: year.toString(),
      month: month.toString(),
    });
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/financial/transactions?${params}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar lançamentos');
    const data: ApiResponse<TransactionsStatement> = await response.json();
    return data.data ?? { openingBalance: 0, closingBalance: 0, transactions: [] };
  }

  /** POST /financial/transactions */
  async createTransaction(payload: TransactionCreatePayload): Promise<FinancialTransaction> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/financial/transactions', payload);
    if (!response.ok) await this.throwApiError(response, 'Falha ao criar lançamento');
    const data: ApiResponse<FinancialTransaction> = await response.json();
    return data.data!;
  }

  /** PUT /financial/transactions/:id?scope=single|group */
  async updateTransaction(id: number, scope: GroupScope, payload: TransactionUpdatePayload): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/financial/transactions/${id}?scope=${scope}`, payload);
    if (!response.ok) await this.throwApiError(response, 'Falha ao atualizar lançamento');
  }

  /**
   * PATCH /financial/transactions/:id/supplier-link — vincula o lançamento a
   * um fornecedor e, opcionalmente, ao pedido de compra que ele paga.
   */
  async linkTransactionSupplier(id: number, body: TransactionSupplierLinkPayload): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.patch(`/financial/transactions/${id}/supplier-link`, body);
    if (!response.ok) await this.throwApiError(response, 'Falha ao vincular lançamento ao fornecedor');
  }

  /** DELETE /financial/transactions/:id?scope=single|group */
  async deleteTransaction(id: number, scope: GroupScope): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/financial/transactions/${id}?scope=${scope}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao excluir lançamento');
  }

  /** POST /financial/accounts/:idAccountsMain/balances — "Registrar Saldo" */
  async registerBalance(idAccountsMain: number, payload: { balanceDate: string; amount: number }): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/financial/accounts/${idAccountsMain}/balances`, payload);
    if (!response.ok) await this.throwApiError(response, 'Falha ao registrar saldo');
  }

  // uploadCieloFile foi REMOVIDO: o endpoint responde 410. A planilha da Cielo
  // agora passa pela conferencia em Relatorios -> Financeiro -> Recebiveis de
  // Cartao, que compara com a agenda antes de lancar e nao duplica.

  /** GET /financial/transactions/export?accountId=&year=&month= — download CSV */
  async exportTransactionsCsv(accountId: number, year: number, month: number): Promise<void> {
    const params = new URLSearchParams({
      accountId: accountId.toString(),
      year: year.toString(),
      month: month.toString(),
    });
    await this.downloadFile(
      `/financial/transactions/export?${params}`,
      `conta-corrente-${year}-${String(month).padStart(2, '0')}.csv`,
    );
  }

  // ─── Cartões de Crédito ────────────────────────────────────────

  /** GET /financial/credit-cards?companyId= */
  async getCreditCards(companyId: number): Promise<CreditCard[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/financial/credit-cards?companyId=${companyId}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar cartões');
    const data: ApiResponse<CreditCard[]> = await response.json();
    return data.data ?? [];
  }

  /** GET /financial/credit-cards/:id */
  async getCreditCard(id: number): Promise<CreditCard> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/financial/credit-cards/${id}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar cartão');
    const data: ApiResponse<CreditCard> = await response.json();
    return data.data!;
  }

  /** POST /financial/credit-cards */
  async createCreditCard(payload: CreditCardFormData): Promise<CreditCard> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/financial/credit-cards', payload);
    if (!response.ok) await this.throwApiError(response, 'Falha ao cadastrar cartão');
    const data: ApiResponse<CreditCard> = await response.json();
    return data.data!;
  }

  /** PUT /financial/credit-cards/:id */
  async updateCreditCard(id: number, payload: CreditCardFormData): Promise<CreditCard> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/financial/credit-cards/${id}`, payload);
    if (!response.ok) await this.throwApiError(response, 'Falha ao atualizar cartão');
    const data: ApiResponse<CreditCard> = await response.json();
    return data.data!;
  }

  /** DELETE /financial/credit-cards/:id (só cartão sem uso) */
  async deleteCreditCard(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/financial/credit-cards/${id}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao excluir cartão');
  }

  /** GET /financial/credit-card-invoices?creditCardId= */
  /** GET /financial/credit-cards/projection — matriz de parcelas × meses (todos os cartões/empresas) */
  async getCreditCardProjection(): Promise<CreditCardProjection> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/financial/credit-cards/projection');
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar a projeção de cartões');
    const data: ApiResponse<CreditCardProjection> = await response.json();
    return data.data ?? { months: [], cards: [], groups: [], others: [] };
  }

  async getCreditCardInvoices(creditCardId: number): Promise<CreditCardInvoicesResponse> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/financial/credit-card-invoices?creditCardId=${creditCardId}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar faturas');
    const data: ApiResponse<CreditCardInvoicesResponse> = await response.json();
    return data.data ?? { currentInvoiceId: null, invoices: [] };
  }

  /** GET /financial/credit-card-invoices/:id/transactions */
  async getInvoiceTransactions(invoiceId: number): Promise<CreditCardInvoiceTransactionsResponse> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/financial/credit-card-invoices/${invoiceId}/transactions`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar lançamentos da fatura');
    const data: ApiResponse<CreditCardInvoiceTransactionsResponse> = await response.json();
    return data.data ?? { total: 0, transactions: [] };
  }

  /** POST /financial/credit-card-transactions */
  async createCreditCardTransaction(payload: CreditCardTransactionCreatePayload): Promise<CreditCardTransaction> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/financial/credit-card-transactions', payload);
    if (!response.ok) await this.throwApiError(response, 'Falha ao criar lançamento de cartão');
    const data: ApiResponse<CreditCardTransaction> = await response.json();
    return data.data!;
  }

  /** PUT /financial/credit-card-transactions/:id?scope=single|group */
  async updateCreditCardTransaction(id: number, scope: GroupScope, payload: CreditCardTransactionUpdatePayload): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/financial/credit-card-transactions/${id}?scope=${scope}`, payload);
    if (!response.ok) await this.throwApiError(response, 'Falha ao atualizar lançamento de cartão');
  }

  /** DELETE /financial/credit-card-transactions/:id?scope=single|group */
  async deleteCreditCardTransaction(id: number, scope: GroupScope): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/financial/credit-card-transactions/${id}?scope=${scope}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao excluir lançamento de cartão');
  }

  /** POST /financial/credit-card-invoices/:id/upload (multipart) — revisão da fatura */
  async uploadInvoiceFile(invoiceId: number, file: File): Promise<InvoiceUploadReview> {
    const fd = new FormData();
    fd.append('file', file);
    const response = await this.postMultipart(
      `/financial/credit-card-invoices/${invoiceId}/upload`,
      fd,
      'Falha ao enviar arquivo da fatura',
    );
    const data: ApiResponse<InvoiceUploadReview> = await response.json();
    return data.data ?? { new: [], found: [], missingFromFile: [] };
  }

  /** POST /financial/credit-card-invoices/:id/confirm-upload */
  async confirmInvoiceUpload(invoiceId: number, transactions: InvoiceUploadConfirmItem[]): Promise<InvoiceUploadConfirmResult> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/financial/credit-card-invoices/${invoiceId}/confirm-upload`, { transactions });
    if (!response.ok) await this.throwApiError(response, 'Falha ao confirmar importação da fatura');
    const data: ApiResponse<InvoiceUploadConfirmResult> = await response.json();
    return data.data ?? { insertedCount: 0, warnings: [] };
  }

  /** GET /financial/credit-card-invoices/:id/export — download CSV */
  async exportInvoiceCsv(invoiceId: number): Promise<void> {
    await this.downloadFile(`/financial/credit-card-invoices/${invoiceId}/export`, `fatura-${invoiceId}.csv`);
  }

  // ─── Lançamentos Recorrentes ───────────────────────────────────

  /** GET /financial/recurring-transactions?companyId=&type=1|2[&idAccountsMain][&idCreditCards] */
  async getRecurringTransactions(
    companyId: number,
    type: RecurringType,
    filters?: { idAccountsMain?: number; idCreditCards?: number },
  ): Promise<RecurringTransaction[]> {
    const params = new URLSearchParams({
      companyId: companyId.toString(),
      type: type.toString(),
    });
    if (filters?.idAccountsMain != null) params.set('idAccountsMain', String(filters.idAccountsMain));
    if (filters?.idCreditCards != null) params.set('idCreditCards', String(filters.idCreditCards));

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/financial/recurring-transactions?${params}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar lançamentos recorrentes');
    const data: ApiResponse<RecurringTransaction[]> = await response.json();
    return data.data ?? [];
  }

  /** POST /financial/recurring-transactions */
  async createRecurringTransaction(payload: RecurringTransactionPayload): Promise<RecurringTransaction> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/financial/recurring-transactions', payload);
    if (!response.ok) await this.throwApiError(response, 'Falha ao criar lançamento recorrente');
    const data: ApiResponse<RecurringTransaction> = await response.json();
    return data.data!;
  }

  /** PUT /financial/recurring-transactions/:id */
  async updateRecurringTransaction(id: number, payload: RecurringTransactionPayload): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/financial/recurring-transactions/${id}`, payload);
    if (!response.ok) await this.throwApiError(response, 'Falha ao atualizar lançamento recorrente');
  }

  /** DELETE /financial/recurring-transactions/:id?mode=0|1|2 */
  async deleteRecurringTransaction(id: number, mode: RecurringDeleteMode): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/financial/recurring-transactions/${id}?mode=${mode}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao excluir lançamento recorrente');
  }

  // ─── Importação de Extrato ─────────────────────────────────────

  /** POST /financial/transaction-imports (multipart CSV) */
  async importStatement(file: File, companyId: number, idAccountsMain: number): Promise<StatementImportResult> {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('companyId', String(companyId));
    fd.append('idAccountsMain', String(idAccountsMain));
    const response = await this.postMultipart('/financial/transaction-imports', fd, 'Falha ao importar extrato');
    const data: ApiResponse<StatementImportResult> = await response.json();
    return data.data ?? { insertedCount: 0, missingFromFile: [] };
  }

  /** GET /financial/import-correlations[?companyId=] */
  async getImportCorrelations(companyId?: number): Promise<ImportCorrelation[]> {
    const suffix = companyId != null ? `?companyId=${companyId}` : '';
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/financial/import-correlations${suffix}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar correlações de importação');
    const data: ApiResponse<ImportCorrelation[]> = await response.json();
    return data.data ?? [];
  }

  /** POST /financial/import-correlations */
  async createImportCorrelation(payload: ImportCorrelationPayload): Promise<ImportCorrelation> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/financial/import-correlations', payload);
    if (!response.ok) await this.throwApiError(response, 'Falha ao criar correlação');
    const data: ApiResponse<ImportCorrelation> = await response.json();
    return data.data!;
  }

  /** PUT /financial/import-correlations/:id */
  async updateImportCorrelation(id: number, payload: ImportCorrelationPayload): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/financial/import-correlations/${id}`, payload);
    if (!response.ok) await this.throwApiError(response, 'Falha ao atualizar correlação');
  }

  /** DELETE /financial/import-correlations/:id */
  async deleteImportCorrelation(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/financial/import-correlations/${id}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao excluir correlação');
  }

  // ─── Recebimentos PIX ──────────────────────────────────────────

  /** GET /financial/pix-receivables?companyId=&orderId=&proposalId=&from=&to= */
  async getPixReceivables(filters: {
    companyId?: number;
    orderId?: number;
    proposalId?: number;
    from?: string;
    to?: string;
  }): Promise<PixReceivable[]> {
    const params = new URLSearchParams();
    if (filters.companyId != null) params.set('companyId', String(filters.companyId));
    if (filters.orderId != null) params.set('orderId', String(filters.orderId));
    if (filters.proposalId != null) params.set('proposalId', String(filters.proposalId));
    if (filters.from) params.set('from', filters.from);
    if (filters.to) params.set('to', filters.to);
    const qs = params.toString();
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/financial/pix-receivables${qs ? `?${qs}` : ''}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar recebimentos PIX');
    const data: ApiResponse<PixReceivable[]> = await response.json();
    return data.data ?? [];
  }

  /** GET /financial/pix-receivables/fee-summary?companyId=&month=AAAA-MM */
  async getPixFeeSummary(companyId: number, month: string): Promise<PixFeeSummary> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/financial/pix-receivables/fee-summary?companyId=${companyId}&month=${month}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar o comparativo de tarifas PIX');
    const data: ApiResponse<PixFeeSummary> = await response.json();
    return data.data!;
  }

  // ─── Plano de Contas ───────────────────────────────────────────

  /** GET /financial/accounting-plans?companyId=&includeDeleted=false — árvore */
  async getAccountingPlans(companyId: number, includeDeleted = false): Promise<AccountingPlanNode[]> {
    const params = new URLSearchParams({
      companyId: companyId.toString(),
      includeDeleted: String(includeDeleted),
    });
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/financial/accounting-plans?${params}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar plano de contas');
    const data: ApiResponse<AccountingPlanNode[]> = await response.json();
    return data.data ?? [];
  }

  /** GET /financial/dre-groups */
  async getDreGroups(): Promise<DreGroup[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/financial/dre-groups');
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar grupos DRE');
    const data: ApiResponse<DreGroup[]> = await response.json();
    return data.data ?? [];
  }

  /** POST /financial/accounting-plans */
  async createAccountingPlan(payload: AccountingPlanCreatePayload): Promise<AccountingPlanNode> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/financial/accounting-plans', payload);
    if (!response.ok) await this.throwApiError(response, 'Falha ao criar categoria');
    const data: ApiResponse<AccountingPlanNode> = await response.json();
    return data.data!;
  }

  /** PUT /financial/accounting-plans/:id */
  async updateAccountingPlan(id: number, payload: AccountingPlanUpdatePayload): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/financial/accounting-plans/${id}`, payload);
    if (!response.ok) await this.throwApiError(response, 'Falha ao atualizar categoria');
  }

  /** DELETE /financial/accounting-plans/:id — 409 traz mensagem de conflito da API */
  async deleteAccountingPlan(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/financial/accounting-plans/${id}`);
    if (!response.ok) {
      await this.throwApiError(
        response,
        response.status === 409
          ? 'Categoria em uso — não é possível excluir'
          : 'Falha ao excluir categoria',
      );
    }
  }

  /** PUT /financial/accounting-plans/:id/dre-group */
  async setDreGroup(id: number, dreGroupId: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/financial/accounting-plans/${id}/dre-group`, { dreGroupId });
    if (!response.ok) await this.throwApiError(response, 'Falha ao definir grupo DRE');
  }

  /** DELETE /financial/accounting-plans/:id/dre-group — volta a herdar do pai */
  async clearDreGroup(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/financial/accounting-plans/${id}/dre-group`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao remover grupo DRE');
  }

  /** GET /financial/accounting-plans/unmapped — lista plana sem classificação DRE */
  async getUnmappedAccountingPlans(): Promise<AccountingPlanNode[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/financial/accounting-plans/unmapped');
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar categorias sem classificação');
    const data: ApiResponse<AccountingPlanNode[]> = await response.json();
    return data.data ?? [];
  }

  // ─── Margem de Produtos ────────────────────────────────────────

  /** GET /financial/product-earn */
  async getProductEarn(): Promise<ProductEarnRow[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/financial/product-earn');
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar margem de produtos');
    const data: ApiResponse<ProductEarnRow[]> = await response.json();
    return data.data ?? [];
  }
}

export const financialService = new FinancialService();
export default financialService;
